import { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { BRAND } from "@/lib/brand";
import { Loader2 } from "lucide-react";

export default function LoginPage() {
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!user) return;
    const target = user.role === "admin" ? "/admin" : "/dashboard";
    navigate(target, { replace: true });
  }, [navigate, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !password) {
      setError("Please enter email and password.");
      return;
    }
    setLoading(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      const redirectTo =
        location.state?.from && location.state.from !== "/login"
          ? location.state.from
          : u.role === "admin"
            ? "/admin"
            : "/dashboard";
      navigate(redirectTo, { replace: true });
    } catch (err) {
      const msg =
        err?.response?.data?.detail ||
        (err?.response?.status === 401
          ? "Invalid email or password."
          : "Unable to sign in. Please try again.");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4"
      style={{
        background: `linear-gradient(135deg, ${BRAND.teal}15 0%, ${BRAND.purple}15 100%)`,
      }}
    >
      <div
        className="w-full max-w-md bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-200"
        data-testid="login-card"
      >
        {/* Brand header */}
        <div
          className="px-8 pt-8 pb-6 text-center"
          style={{
            background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
          }}
        >
          <img
            src={BRAND.logoUrl}
            alt="Wellform MD"
            className="h-24 w-24 mx-auto object-contain bg-white rounded-xl p-2 shadow-md"
            draggable="false"
          />
          <h1 className="mt-4 text-xl font-bold text-white tracking-wide">
            Wellform MD Timesheet
          </h1>
          <p className="mt-1 text-sm text-white/80">
            Sign in to log your time
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-5" data-testid="login-form">
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
              Email
            </label>
            <input
              id="email"
              type="email"
              autoComplete="email"
              data-testid="email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@wellformmd.com"
              className="w-full px-3.5 py-2.5 rounded-lg border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 transition"
              style={{ "--tw-ring-color": BRAND.teal }}
              disabled={loading}
            />
          </div>

          <div>
            <div className="mb-1.5">
              <label htmlFor="password" className="block text-sm font-medium text-slate-700">
                Password
              </label>
            </div>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                autoComplete="current-password"
                data-testid="password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="w-full px-3.5 py-2.5 pr-16 rounded-lg border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 transition"
                style={{ "--tw-ring-color": BRAND.teal }}
                disabled={loading}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                data-testid="toggle-password"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-500 hover:text-slate-700 px-2 py-1"
                tabIndex={-1}
              >
                {showPassword ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          {error && (
            <div
              className="text-sm px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit"
            className="w-full py-2.5 rounded-lg text-white font-semibold shadow-sm transition disabled:opacity-70 disabled:cursor-not-allowed"
            style={{
              background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
            }}
          >
            {loading ? (
              <span className="inline-flex items-center gap-2 justify-center">
                <Loader2 className="animate-spin" size={16} />
                Signing in…
              </span>
            ) : (
              "Sign in"
            )}
          </button>

          <div className="text-center">
            <Link
              to="/forgot-password"
              className="text-sm font-medium text-slate-500 hover:text-slate-700"
            >
              Forgot password?
            </Link>
          </div>
        </form>

        <div className="px-8 pb-6 text-center text-xs text-slate-400">
          © {new Date().getFullYear()} Wellform MD · Weight Loss & Wellness
        </div>
      </div>
    </div>
  );
}
